import { Navigate } from 'react-router-dom';
import { COMPANY_SCOPE, useSelection } from '@/app/SelectionContext';
import { useKpis, usePnlSource } from '@/app/hooks';
import { PageHeader } from '@/components/PageHeader';
import { ExportMenu } from '@/components/ExportMenu';
import { KPICard } from '@/components/KPICard';
import { Card, SectionTitle } from '@/components/Card';
import { KpiGridSkeleton } from '@/components/LoadingScreen';
import { ErrorState } from '@/components/ErrorState';
import { NoFinancialData } from '@/components/NoFinancialData';
import { SourceReportTable, YTD_LABEL } from '@/components/SourceReportTable';
import { useIsWideScreen } from '@/app/useIsWideScreen';
import { Icon } from '@/components/Icon';
import { ChartCard, ExpenseStackChart, SingleSeriesChart } from '@/components/charts';
import { buildPoints } from '@/utils/series';
import { MONTHS_RU, NO_DATA, formatAmd, periodLabel, periodShortLabel, toNumber } from '@/utils/format';
import { font, v } from '@/theme/tokens';
import type { CostCenterAnalytics, ExpenseDriverRow } from '@/types/schemas';

const ALL_EXPENSES_LABEL = 'Все расходы Гераци';

/** Group colours are assigned by position, so a group the workbook adds later
 * still gets one without a code change. */
const GROUP_COLORS = [
  'var(--chart-violet)',
  'var(--chart-blue)',
  'var(--chart-teal)',
  'var(--chart-amber)',
  'var(--chart-cyan)',
  'var(--chart-lime)',
  'var(--chart-pink)',
  'var(--chart-slate)',
];

function DriverList({ rows, tone }: { rows: ExpenseDriverRow[]; tone: 'up' | 'down' }) {
  if (rows.length === 0) {
    return <div style={{ fontSize: 13, color: v('ink-muted') }}>Нет сопоставимых статей</div>;
  }
  const color = tone === 'up' ? v('negative') : v('positive');
  return (
    <div style={{ display: 'grid', gap: 10 }}>
      {rows.map((row) => {
        const delta = toNumber(row.delta);
        return (
          <div key={row.category_id} style={{ display: 'flex', alignItems: 'baseline', gap: 10 }}>
            <span style={{ flex: 1, fontSize: 13 }}>{row.name}</span>
            <span style={{ fontFamily: font.mono, fontSize: 13, fontWeight: 700, color }}>
              {delta === null ? NO_DATA : `${delta >= 0 ? '+' : '−'}${formatAmd(Math.abs(delta))}`}
            </span>
          </div>
        );
      })}
    </div>
  );
}

function CostCenterView({ data }: { data: CostCenterAnalytics }) {
  const labels = data.monthly_series.map((point) => periodShortLabel(point.month));
  const allExpenses = data.monthly_series.map((point) => toNumber(point.all_expenses));

  const stack = data.groups.map((group, index) => ({
    key: group.name,
    color: GROUP_COLORS[index % GROUP_COLORS.length],
    series: data.monthly_series.map((point) => toNumber(point.groups[group.code] ?? null)),
  }));

  const ytdRows = [
    { code: 'ALL_EXPENSES', name: ALL_EXPENSES_LABEL, amount: data.ytd.all_expenses },
    ...data.groups.map((group) => ({
      code: group.code,
      name: group.name,
      amount: data.ytd.groups[group.code] ?? null,
    })),
  ];

  return (
    <>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(220px, 100%), 1fr))', gap: 14 }}>
        <KPICard
          label={ALL_EXPENSES_LABEL}
          value={formatAmd(toNumber(data.all_expenses.amount))}
          change={toNumber(data.all_expenses.mom_pct)}
          series={allExpenses}
          color="var(--chart-violet)"
          invert
        />
        {data.groups.map((group, index) => (
          <KPICard
            key={group.code}
            label={group.name}
            value={formatAmd(toNumber(group.amount))}
            change={toNumber(group.mom_pct)}
            color={GROUP_COLORS[index % GROUP_COLORS.length]}
            invert
          />
        ))}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(340px, 100%), 1fr))', gap: 16, marginTop: 16 }}>
        <ChartCard title="Динамика всех расходов">
          <SingleSeriesChart
            data={buildPoints(labels, { [ALL_EXPENSES_LABEL]: allExpenses })}
            name={ALL_EXPENSES_LABEL}
            color="var(--chart-violet)"
          />
        </ChartCard>
        {stack.length > 0 && (
          <ChartCard title="Структура затрат по месяцам">
            <ExpenseStackChart
              data={buildPoints(labels, Object.fromEntries(stack.map((s) => [s.key, s.series])))}
              series={stack.map(({ key, color }) => ({ key, color }))}
            />
          </ChartCard>
        )}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(300px, 100%), 1fr))', gap: 16, marginTop: 16 }}>
        <Card>
          <SectionTitle>Главные драйверы роста затрат</SectionTitle>
          <DriverList rows={data.top_drivers_up} tone="up" />
        </Card>
        <Card>
          <SectionTitle>Наибольшее снижение затрат</SectionTitle>
          <DriverList rows={data.top_drivers_down} tone="down" />
        </Card>
        <Card>
          <SectionTitle>{ytdTitle(data)}</SectionTitle>
          <div style={{ display: 'grid', gap: 10 }}>
            {ytdRows.map((row) => (
              <div key={row.code} style={{ display: 'flex', alignItems: 'baseline', gap: 10 }}>
                <span style={{ flex: 1, fontSize: 13, fontWeight: row.code === 'ALL_EXPENSES' ? 700 : 500 }}>
                  {row.name}
                </span>
                <span style={{ fontFamily: font.mono, fontSize: 13, fontWeight: row.code === 'ALL_EXPENSES' ? 700 : 500 }}>
                  {formatAmd(toNumber(row.amount))}
                </span>
              </div>
            ))}
          </div>
        </Card>
      </div>

    </>
  );
}


/** "Итоги 2026 · Январь–Июль" — the span of confirmed months, not the calendar
 * year, so an unconfirmed August never appears in the heading. */
function ytdTitle(data: CostCenterAnalytics): string {
  const points = data.monthly_series;
  if (points.length === 0) return 'Итоги года';
  const first = points[0];
  const last = points[points.length - 1];
  const span = first.month === last.month
    ? MONTHS_RU[first.month - 1]
    : `${MONTHS_RU[first.month - 1]}–${MONTHS_RU[last.month - 1]}`;
  return `Итоги ${last.year} · ${span}`;
}

export function ProductionPage() {
  const { selectedUnit, selectedPeriod, scope, units } = useSelection();
  const kitchen = selectedUnit?.type === 'PRODUCTION_KITCHEN'
    ? selectedUnit
    : units.find((u) => u.type === 'PRODUCTION_KITCHEN') ?? null;

  const query = useKpis(kitchen?.id, selectedPeriod?.id);

  if (scope === COMPANY_SCOPE && !kitchen) return <Navigate to="/company" replace />;

  if (!kitchen) {
    return (
      <div>
        <PageHeader title="Производство" subtitle="Производственный цех не создан" />
        <NoFinancialData scope="production" />
      </div>
    );
  }

  return (
    <div style={{ animation: 'fade-in 0.35s ease' }}>
      <PageHeader
        title={`Производство «${kitchen.name}»`}
        subtitle={
          selectedPeriod
            ? `Производственный цех · ${periodLabel(selectedPeriod.year, selectedPeriod.month)}`
            : 'Производственный цех'
        }
        actions={
          <ExportMenu
            report="production"
            params={{ businessUnitId: kitchen.id, periodId: selectedPeriod?.id }}
          />
        }
      />

      <div style={{ display: 'flex', gap: 10, alignItems: 'center', padding: '12px 16px', background: v('accent-soft'), color: v('accent-ink'), borderRadius: 12, marginBottom: 18, fontSize: 13 }}>
        <Icon name="factory" size={16} />
        Это производственный цех, а не ресторан. Выручка, прибыль и маржа для него не рассчитываются — страница показывает только аналитику расходов.
      </div>

      {query.isPending && <KpiGridSkeleton count={6} />}
      {query.error && (
        <ErrorState
          message={query.error instanceof Error ? query.error.message : 'Ошибка загрузки'}
          onRetry={() => void query.refetch()}
        />
      )}

      {query.data && !query.data.has_data && <NoFinancialData unitName={kitchen.name} scope="production" />}

      {query.data?.has_data && query.data.cost_center && (
        <CostCenterView data={query.data.cost_center} />
      )}

      {query.data?.has_data && <GeratsiSourceReport unitId={kitchen.id} />}
    </div>
  );
}

/**
 * The confirmed «Гераци» sheet, mirrored.
 *
 * Separate from everything above it on purpose: the cards, charts and YTD are
 * analytics computed from the canonical model, while this is the report the
 * owner uploaded, rendered row for row. Neither feeds the other.
 *
 * Kept off phones — a worksheet this wide cannot be read on one, and the page
 * already gives the phone its cards, chart and drivers.
 */
function GeratsiSourceReport({ unitId }: { unitId: string }) {
  const { selectedPeriod } = useSelection();
  const wide = useIsWideScreen();
  const year = selectedPeriod?.year ?? new Date().getFullYear();
  const query = usePnlSource(wide ? unitId : undefined, year);

  if (!wide || !query.data || query.data.rows.length === 0) return null;

  const periodKey = selectedPeriod
    ? `${selectedPeriod.year}-${String(selectedPeriod.month).padStart(2, '0')}`
    : null;

  return (
    <div style={{ marginTop: 16 }}>
      <SectionTitle>Отчёт по расходам · из подтверждённого файла</SectionTitle>
      <Card>
        <SourceReportTable
          rows={query.data.rows}
          periods={query.data.periods}
          selectedKey={periodKey}
        />
        <div style={{ fontSize: 12, color: v('ink-muted'), marginTop: 10 }}>
          Таблица показана как в подтверждённом файле «{query.data.filename}»: порядок строк, названия и
          значения — из листа «{query.data.sheet_name}», без пересчёта и перегруппировки. Колонка
          «{YTD_LABEL}» суммируется из показанных месяцев. Показатели и графики выше считаются отдельно,
          по канонической модели.
        </div>
      </Card>
    </div>
  );
}
